import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Chip, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Tooltip, Divider,
} from "@mui/material";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import { useNotifier } from "../../../Core/Notifier";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

type TabKey = "items" | "receiving" | "payments";

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  paid: { bg: "#D1FAE5", color: "#059669" }, partial: { bg: "#FEF3C7", color: "#D97706" }, unpaid: { bg: "#FEE2E2", color: "#DC2626" },
  pending: { bg: "#F3F4F6", color: "#6B7280" }, completed: { bg: "#D1FAE5", color: "#059669" }, "n/a": { bg: "#F3F4F6", color: "#6B7280" },
  received: { bg: "#D1FAE5", color: "#059669" },
};

const PODetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("items");
  const [duplicating, setDuplicating] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${API_URL}/purchase-order/${id}/detail`, { headers: getHeaders() })
      .then(r => r.json()).then(j => { if (j.success) setData(j.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await fetch(`${API_URL}/purchase-order/${id}/duplicate`, { method: "POST", headers: getHeaders() });
      const json = await res.json();
      if (json.success) { showMessage("PO duplicated"); navigate(`/po-detail/${json.data.id}`); }
    } catch { showErrorMessage("Failed"); } finally { setDuplicating(false); }
  };

  if (loading) return <PageLoader height="60vh" />;
  if (!data) return <Box sx={{ textAlign: "center", py: 10 }}><Typography color="#6B7280">PO not found</Typography></Box>;

  const sm = data.summary;
  const psc = STATUS_COLORS[data.payment_status] || STATUS_COLORS.unpaid;
  const rsc = STATUS_COLORS[data.receiving_status] || STATUS_COLORS.pending;

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #F3F4F6", px: 3, py: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ width: 28, height: 28 }}><ArrowBackIcon sx={{ fontSize: 16, color: "#9CA3AF" }} /></IconButton>
          <Typography fontSize={12} color="#9CA3AF" sx={{ cursor: "pointer" }} onClick={() => navigate("/bill/list")}>Purchase Orders</Typography>
          <Typography fontSize={12} color="#D1D5DB">/</Typography>
          <Typography fontSize={12} color="#374151" fontWeight={600}>{data.po_number}</Typography>
        </Box>
      </Box>

      {/* Hero Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3, py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography fontSize={28} fontWeight={700} color="#111827" letterSpacing="-0.02em">{data.po_number}</Typography>
              <Chip label={data.receiving_status} size="small" sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: rsc.bg, color: rsc.color, textTransform: "capitalize" }} />
              <Chip label={data.payment_status} size="small" sx={{ height: 24, fontSize: 10, fontWeight: 700, bgcolor: psc.bg, color: psc.color, textTransform: "capitalize" }} />
            </Box>
            <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
              <Typography fontSize={13} color="#6B7280"><strong>Supplier:</strong> {data.supplier_name}</Typography>
              <Typography fontSize={13} color="#6B7280"><strong>Date:</strong> {data.order_date ? new Date(data.order_date).toLocaleDateString() : "—"}</Typography>
              {data.expected_delivery && <Typography fontSize={13} color="#6B7280"><strong>Expected:</strong> {new Date(data.expected_delivery).toLocaleDateString()}</Typography>}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => navigate(`/purchase-order/receive/${id}`)}
              startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
              Receive
            </Button>
            {sm.balance > 0 && (
              <Button size="small" variant="contained" disableElevation
                onClick={() => navigate(`/procurement/payments`)}
                startIcon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }}>
                Make Payment
              </Button>
            )}
            <Button size="small" variant="outlined" onClick={handleDuplicate} disabled={duplicating}
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#E5E7EB", color: "#374151" }}>
              Duplicate
            </Button>
          </Box>
        </Box>

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[
            { label: "Order Total", value: `$${sm.total_amount.toFixed(2)}`, color: "#111827", bg: "#F9FAFB", icon: <ReceiptLongIcon /> },
            { label: "Paid", value: `$${sm.paid_amount.toFixed(2)}`, color: "#059669", bg: "#F0FDF4", icon: <AttachMoneyIcon /> },
            { label: "Balance", value: `$${sm.balance.toFixed(2)}`, color: sm.balance > 0 ? "#DC2626" : "#059669", bg: sm.balance > 0 ? "#FEF2F2" : "#F0FDF4", icon: <WarningAmberIcon /> },
            { label: "Ordered", value: String(sm.total_ordered), color: "#2563EB", bg: "#EFF6FF", icon: <InventoryIcon /> },
            { label: "Received", value: `${sm.total_received} (${sm.receive_pct}%)`, color: "#059669", bg: "#F0FDF4", icon: <CheckCircleIcon /> },
          ].map((kpi, i) => (
            <Grid item xs={6} sm={2.4} key={i}>
              <Box sx={{ p: 2, borderRadius: 2, border: "1px solid #E5E7EB", bgcolor: kpi.bg }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  {React.cloneElement(kpi.icon, { sx: { fontSize: 14, color: kpi.color } })}
                  <Typography fontSize={10} color="#9CA3AF" fontWeight={600} textTransform="uppercase">{kpi.label}</Typography>
                </Box>
                <Typography fontSize={18} fontWeight={700} color={kpi.color} sx={{ fontVariantNumeric: "tabular-nums" }}>{kpi.value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Receiving Progress */}
        {sm.total_ordered > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography fontSize={11} color="#6B7280">Receiving Progress</Typography>
              <Typography fontSize={11} fontWeight={600} color={sm.receive_pct >= 100 ? "#059669" : "#D97706"}>{sm.receive_pct}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={sm.receive_pct}
              sx={{ height: 6, borderRadius: 3, bgcolor: "#F3F4F6", "& .MuiLinearProgress-bar": { bgcolor: sm.receive_pct >= 100 ? "#059669" : "#00838f", borderRadius: 3 } }} />
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E5E7EB", px: 3 }}>
        <Box sx={{ display: "flex", gap: 0 }}>
          {([
            { key: "items" as TabKey, label: "Items", icon: <InventoryIcon sx={{ fontSize: 15 }} /> },
            { key: "receiving" as TabKey, label: "Receiving", icon: <LocalShippingIcon sx={{ fontSize: 15 }} /> },
            { key: "payments" as TabKey, label: "Payments", icon: <AttachMoneyIcon sx={{ fontSize: 15 }} /> },
          ]).map(t => (
            <Box key={t.key} onClick={() => setTab(t.key)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75, px: 2, py: 1.5, cursor: "pointer",
                borderBottom: tab === t.key ? "2px solid #111827" : "2px solid transparent",
                color: tab === t.key ? "#111827" : "#9CA3AF",
                "&:hover": { color: "#374151" },
              }}>
              {t.icon}
              <Typography fontSize={13} fontWeight={tab === t.key ? 600 : 500}>{t.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 3 }}>
        {/* ITEMS */}
        {tab === "items" && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                  {["Product", "Unit", "Ordered", "Received", "Remaining", "Unit Price", "Total", "Status"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>
                  ))}
                </TableRow></TableHead>
                <TableBody>
                  {(data.items || []).map((item: any, i: number) => {
                    const isc = STATUS_COLORS[item.status] || STATUS_COLORS.pending;
                    return (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                        <TableCell><Typography fontSize={13} fontWeight={600} color="#111827" sx={{ textTransform: "capitalize" }}>{item.product_name}</Typography></TableCell>
                        <TableCell><Chip label={item.unit || "—"} size="small" sx={{ height: 20, fontSize: 10, bgcolor: "#F3F4F6" }} /></TableCell>
                        <TableCell><Typography fontSize={13} fontWeight={700}>{item.ordered_qty}</Typography></TableCell>
                        <TableCell><Typography fontSize={13} fontWeight={700} color="#059669">{item.received_qty}</Typography></TableCell>
                        <TableCell><Typography fontSize={13} fontWeight={600} color={item.remaining_qty > 0 ? "#D97706" : "#059669"}>{item.remaining_qty}</Typography></TableCell>
                        <TableCell><Typography fontSize={12} color="#6B7280">${item.unit_price.toFixed(2)}</Typography></TableCell>
                        <TableCell><Typography fontSize={13} fontWeight={700}>${item.total.toFixed(2)}</Typography></TableCell>
                        <TableCell><Chip label={item.status} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: isc.bg, color: isc.color, textTransform: "capitalize" }} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* RECEIVING */}
        {tab === "receiving" && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", overflow: "hidden" }}>
            {(data.receipts || []).length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    {["Date", "GRN #", "Qty Received", "Damaged", "Notes"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {data.receipts.map((r: any, i: number) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(r.date).toLocaleDateString()}</TableCell>
                        <TableCell><Typography fontSize={12} fontFamily="monospace" color="#2563EB">{r.grn_number || "—"}</Typography></TableCell>
                        <TableCell><Typography fontSize={13} fontWeight={700} color="#059669">+{r.received_qty}</Typography></TableCell>
                        <TableCell><Typography fontSize={12} color={r.damaged_qty > 0 ? "#DC2626" : "#D1D5DB"}>{r.damaged_qty || "—"}</Typography></TableCell>
                        <TableCell sx={{ fontSize: 11, color: "#9CA3AF" }}>{r.notes || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <LocalShippingIcon sx={{ fontSize: 40, color: "#D1D5DB", mb: 1 }} />
                <Typography fontSize={14} color="#6B7280">No items received yet</Typography>
                <Button size="small" variant="contained" disableElevation onClick={() => navigate(`/purchase-order/receive/${id}`)}
                  sx={{ mt: 2, borderRadius: 1.5, textTransform: "none", bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
                  Start Receiving
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* PAYMENTS */}
        {tab === "payments" && (
          <Box sx={{ bgcolor: "#fff", borderRadius: 2, border: "1px solid #E5E7EB", overflow: "hidden" }}>
            {(data.payments || []).length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#F9FAFB" }}>
                    {["Date", "Amount", "Method", "Reference"].map(h => (
                      <TableCell key={h} sx={{ fontSize: 11, fontWeight: 500, color: "#6B7280" }}>{h}</TableCell>
                    ))}
                  </TableRow></TableHead>
                  <TableBody>
                    {data.payments.map((p: any, i: number) => (
                      <TableRow key={i} sx={{ "&:hover": { bgcolor: "#F9FAFB" } }}>
                        <TableCell sx={{ fontSize: 12 }}>{p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "—"}</TableCell>
                        <TableCell><Typography fontSize={14} fontWeight={700} color="#059669">${(p.allocated_amount || 0).toFixed(2)}</Typography></TableCell>
                        <TableCell><Chip label={p.payment_method?.replace(/_/g, " ") || "—"} size="small" sx={{ height: 20, fontSize: 9, bgcolor: "#F3F4F6", textTransform: "capitalize" }} /></TableCell>
                        <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>{p.reference || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <AttachMoneyIcon sx={{ fontSize: 40, color: "#D1D5DB", mb: 1 }} />
                <Typography fontSize={14} color="#6B7280">No payments recorded</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PODetail;
